import { getCookie } from 'typescript-cookie'
import type { User } from '@/models/user.ts'
import { useUserStore } from '@/stores/user.ts'
import { APIStatusError } from '@/utils/api-client/APIStatusError.ts'
import type { Schema } from '@/models/models.ts'

/**
 * Handles all interactions with the internal API of digola.
 * Should be used for all calls in both, portal and apps.
 */
export class APIClient {
  /**
   * Sends a request to the API and returns the response JSON as an object
   * @param endpoint
   * @param method
   * @param body
   * @param headers
   */
  static async request(
    endpoint: string,
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    body: object = {},
    headers: { string: string } | null = null,
  ): Promise<Response> {
    const cleanEndpoint = endpoint.replace(/^\//, '')
    const response = await fetch(`/api/v1/${cleanEndpoint}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...this.getXSRFHeader(),
        ...(headers ?? {}),
      },
      body: method === 'GET' ? undefined : JSON.stringify(body ?? {}),
      method,
      credentials: 'include',
    })

    if (response.status === 401 || response.status === 419) {
      const userStore = useUserStore()
      if (import.meta.env.DEV) {
        console.log(
          "❌ API request failed with code 401. We're assuming the login expired. The current userStore will be invalidated and the user will be redirected to the login page.",
        )
      }
      userStore.invalidate()
    }

    if (!response.ok) {
      throw new APIStatusError(response)
    }
    return response
  }

  /**
   * Sends a request to the API and returns a parsed model from the JSON
   * @param schema
   * @param endpoint
   * @param method
   * @param body
   * @param headers
   */
  public static async requestWithModel<In, Out>(
    schema: Schema<In, Out>,
    endpoint: string,
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    body: object = {},
    headers: { string: string } | null = null,
  ): Promise<Out> {
    const response = await this.request(endpoint, method, body, headers)
    return schema.parser((await response.json()) as In)
  }

  /**
   * Uploads a file plus fields to the API and returns a parsed model
   * @param schema
   * @param endpoint
   * @param form
   */
  public static async uploadWithModel<In, Out>(
    schema: Schema<In, Out>,
    endpoint: string,
    form: FormData,
  ) {
    const cleanEndpoint = endpoint.replace(/^\//, '')
    const response = await fetch(`/api/v1/${cleanEndpoint}`, {
      headers: {
        Accept: 'application/json',
        ...this.getXSRFHeader(),
      },
      body: form,
      method: 'POST',
      credentials: 'include',
    })

    if (response.status === 401) {
      const userStore = useUserStore()
      if (import.meta.env.DEV) {
        console.log(
          "❌ API request failed with code 401. We're assuming the login expired. The current userStore will be invalidated and the user will be redirected to the login page.",
        )
      }
      userStore.invalidate()
    }

    if (!response.ok) {
      throw new APIStatusError(response)
    }

    return schema.parser((await response.json()) as In)
  }

  /**
   * Refreshes the CSRF cookie from the backend. Should be called before each login attempt.
   */
  public static async refreshCookie(): Promise<void> {
    await fetch('/sanctum/csrf-cookie', {
      credentials: 'include',
    })
  }

  /**
   * Attempts to log in with the given credentials.
   * Returns the logged-in user on success, false if the credentials are wrong.
   *
   * @param username
   * @param password
   * @throws
   */
  public static async login(username: string, password: string): Promise<User | false> {
    const response = await fetch(`login`, {
      body: JSON.stringify({
        username,
        password,
      }),
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...this.getXSRFHeader(),
      },
    })
    if (response.status === 401) {
      return false
    } else if (response.status === 200) {
      return (await response.json()) as User
    }
    return false
  }

  /**
   * Create the current XSRF header based on the token stored in the browser cookies file
   */
  static getXSRFHeader(): object {
    return {
      'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
    }
  }

  /**
   * Sends a GET request to the API and returns the response JSON as an object
   * @param endpoint
   * @param body
   * @param headers
   * @constructor
   */
  public static async GET(
    endpoint: string,
    body: object,
    headers: { string: string } | null,
  ): Promise<Response> {
    return this.request(endpoint, 'GET', body, headers)
  }

  /**
   * Sends a POST request to the API and returns the response JSON as an object
   * @param endpoint
   * @param body
   * @param headers
   * @constructor
   */
  public static async POST(
    endpoint: string,
    body: object,
    headers: { string: string } | null,
  ): Promise<Response> {
    return this.request(endpoint, 'POST', body, headers)
  }

  /**
   * Sends a PUT request to the API and returns the response JSON as an object
   * @param endpoint
   * @param body
   * @param headers
   * @constructor
   */
  public static async PUT(
    endpoint: string,
    body: object,
    headers: { string: string } | null,
  ): Promise<Response> {
    return this.request(endpoint, 'PUT', body, headers)
  }

  /**
   * Sends a DELETE request to the API and returns the response JSON as an object
   * @param endpoint
   * @param body
   * @param headers
   * @constructor
   */
  public static async DELETE(
    endpoint: string,
    body: object,
    headers: { string: string } | null,
  ): Promise<Response> {
    return this.request(endpoint, 'DELETE', body, headers)
  }
}
